import { Position } from "@rio-cloud/rio-uikit/mapTypes";
import { ResponseHere, Route } from "./routes";
import { IRoutesState } from "./routesState";

type TripArr = {
  id: string;
  proposedDate: string;
  status: string;
  startDateTime: string;
  createdAt: string;
  updatedAt: any;
  updatedBy: any;
  driverId: string;
  profileId: any;
  assetId: string;
  deletedAt: any;
  finishedAt: any;
  infringements: [];
  statusAccept: string;
  driverName: string;
  assetName: string;
  driverAvatar: string;
};

type RouteTrip = {
  trips?: TripArr[];
  [key: string]: any;
};

export interface DispatchedDriver {
  driverId: string;
  driverName: string;
  status: "PENDING" | "ACCEPTED" | "PROPOSED" | "REFUSED";
  profileId?: string;
}

export interface EmergencyInfo {
  checklistType?: string;
  dnGroupId?: string | null;
  dnGroupName?: string | null;
  chassis?: string | null;
  vehicleAttended?: string | null;
}

export interface Trip {
  id: string;
  driver: string;
  driverId: string;
  schedule: Schedule;
  statusAcceptance: string;
  status: string;
  origin: string;
  destination: string;
  stops: string;
  distance: string;
  duration: string;
  vehicle: string;
  driverName: string;
  vehicleName: string;
  route?: RouteTrip;
  emergencyInfo?: EmergencyInfo | null;
  dispatchedDrivers?: DispatchedDriver[] | null;
  [key: string]: any;
  profileId?: string;
}

export interface Schedule {
  date: string;
  propose: string;
}

export interface ISelectedDriver {
  id: string;
  uniqueId: string;
  name: string;
  avatar: string;
}

export interface ITripsState {
  idRouteSelected?: string | null;
  idVehicleSelected?: string | null;
  driversSelected: ISelectedDriver[];
  nameVehicleSelected: string | null;
  route?: IRoutesState | null;
  isCustom?: boolean;
  scheduleDateSelected?: string | null;
  scheduleHoursSelected?: string | null;
  idTripSelected?: string | null;
  fetchDetailData?: any;
  routeCostData?: any;
  backup?: any;
  isUptimeEmergency?: boolean;
}

export interface IDriverDispatchInfo {
  id: string;
  name: string;
  profileId?: string;
}

interface ILinkedVehicleBase {
  assetId: string;
  assetName: string;
  routeId: string;
  startDateTime: string;
  startDateTimeUTC?: string;
}

export interface ILinkedVehicleSingleDriver extends ILinkedVehicleBase {
  driverId: string;
  driverName: string;
  profileId?: string;
}

export interface ILinkedVehicleMultiDriver extends ILinkedVehicleBase {
  drivers: IDriverDispatchInfo[];
}

export type ICreateTripRequest = ILinkedVehicleSingleDriver | ILinkedVehicleMultiDriver;

export interface IAcceptTripRequest {
  tripId: string;
  accepted: boolean;
}

export interface ISendTripRequest {
  linkedVehicles: Array<ICreateTripRequest>;
}

export interface ISendTripUpdateRequest {
  [key: string]: any;
}

export interface IDeletedResquest {
  updatedAt: string;
  deletedAt: string;
}

export interface Driver {
  id: string;
  userId?: string;
  name?: string;
  photo?: string;
  profileType?: string;
  avatar?: string;
  type?: string;
  [key: string]: any;
  uniqueProfileId?: string;
  isEnabled?: boolean;
}

export interface IOnGoingTrip {
  tripId: string;
  routeId: string;
  driverId: string;
  driverName: string;
  destinyName: string;
  assetId: string;
  assetName: string;
  distance: string;
  remainingDistance?: string;
  distanceTraveled?: string;
  eta: string;
  suggest?: string | null;
  routeStatusNow: string;
  tripStatusNow: string;
  actualProgress: string;
  polyline: Array<string> | null;
  positions?: Position[];
  dateStarted: string;
  scheduledDateTime?: string;
  latLongActually?: {
    lat: number;
    lng: number;
  };
  departure?: string;
  arrival?: string;
  profileId: string;
  duration?: string;
  hec48hrs?: boolean;
  stopsEta?: IStopEta[];
}

export interface IStopEta {
  routeStopId: string;
  stopName: string;
  order: number;
  category?: string | null;
  estimatedArrival?: string | null;
  actualArrival?: string | null;
  status: string;
}

export interface IPublicStopShared {
  routeStatus: string;
  tripStatus: string;
  isInterrupted: boolean;
  vehicle: {
    assetId: string;
    assetName: string;
  };
  latLongActually?: {
    lat: number;
    lng: number;
  } | null;
  polylinePlanned?: {
    routes: Route[];
  } | null;
  currentPolyline?: string[] | null;
  positions?: Position[] | null;
  stop: IStopEta & {
    lat: number;
    lng: number;
    address: string;
  };
}

export const STOP_ETA_STATUS = {
  PENDING: "PENDING",
  ARRIVED: "ARRIVED",
  DONE: "DONE",
} as const;

export interface ITripStopItem {
  note: string;
  label: string;
  checkpointId?: string;
  order?: number;
  isUnplanned?: boolean;
  routeStopId?: string;
  estimatedArrival?: string | null;
  actualArrival?: string | null;
  stopStatus?: string;
  category?: string | null;
  lat?: number;
  lng?: number;
}

export interface IUrgentAlert {
  id: string;
  tripId: string;
  routeId: string;
  latitude: number;
  longitude: number;
  createdAt: string;
  disarmedAt?: string;
  detectedAt?: string;
  status?: string;
}

export interface DriverTripDetailsResponse {
  driver: string;
  driverId: string;
  profileId: string;
  asset: string;
  performance: PerformanceDetailResponse;
  routes: RouteDetailResponse;
  alertsInfractions: AlertInfractionResponse[];
  desviation: DesviationResponse[];
  responseHere: ResponseHere;
  polyline: string[];
  positions: Position[];
}

export interface SingleMetric {
  name: string;
  planned: string;
  realized: string;
}

export interface PerformanceDetailResponse {
  emitionCO2: SingleMetric;
  fuelConsumption: SingleMetric;
  fuelEfficiency: SingleMetric;
  distance: SingleMetric;
  totalTime: SingleMetric;
  idleTime: SingleMetric;
  routeDeviation: SingleMetric;
}

export interface RouteDetailResponse {
  originName: string;
  destinyName: string;
  startDateTime: string;
  startDateTimePlanned: string;
  endDateTime: string;
  stops: Array<{
    note: string;
    label: string;
  }>;
  originChecklist?: CheckpointStop | null;
  destinyChecklist?: CheckpointStop | null;
}

export interface AlertInfractionResponse {
  name: string;
  type: string;
  time: string;
}

export interface DesviationResponse {
  driverName: string;
  startDate: string;
  endDate: string;
  destiny: string;
  exitAddress?: string | null;
  exitLatitude?: number | null;
  exitLongitude?: number | null;
  returnAddress?: string | null;
  returnLatitude?: number | null;
  returnLongitude?: number | null;
}

export interface SizesStage {
  weight: string | null;
  volume: string | null;
}

export interface StageCheckpointStop {
  id: string;
  name: string;
  comments: null | string;
  sizes: null | SizesStage;
  plannedTimeMinutes: number;
  photoBase64s: string[];
  fields: string[];
  realizedTimeMinutes: null | number;
  index?: number;
}


export interface CheckpointStep {
  index: number;
  id: string;
  title: string;
  estimatedTimeMinutes: number;
  realizedTimeMinutes: number;
  executedAt?: string | null;
  updatedAt: string | null;
  fields: {
    id: string;
    field: {
      id: number;
      title: string;
      description: string;
      type: string;
      name: string;
      options?: string[];
    };
    optionValue: string | null;
    estimatedValue: string | null;
    actualValue: string | null;
    actualValues?: string[] | null;
    required: boolean;
  }[];
}
export interface CheckpointStop {
  id: string;
  address?: string;
  name: string;
  stages: StageCheckpointStop[];
  totalPlannedTimeMinutes?: number;
  title: string;
  isFavorite: boolean;
  checkpointType: string;
  quantity: number;
  estimatedTimeMinutes: number;
  realizedTimeMinutes: number;
  createdAt: string;
  stopFilledAt?: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  userId: string;
  accountId: string;
  steps: CheckpointStep[]
}


export enum ETripStatus {
  PENDING = "PENDING",
  SCHEDULED = "SCHEDULED",
  ACCEPTED = "ACCEPTED",
  REFUSED = "REFUSED",
  INTEREST = "INTEREST",
  AVOID = "AVOID",
  ONROUTE = "ONROUTE",
  DELAYED = "DELAYED",
  OVERDUE = "OVERDUE",
  ESTIMATED = "ESTIMATED",
  OUTROUTE = "OUTROUTE",
  EARLY = "EARLY",
  STARTING = "STARTING",
  NOTSTARTED = "NOTSTARTED",
  PAUSED = "PAUSED",
  CANCELED = "CANCELED",
  FINISHED = "FINISHED",
  SYSTEM_FINISHED = "SYSTEM_FINISHED",
  STARTED = "STARTED",
  INTERRUPTED = 'INTERRUPTED',
  MANUALLY_FINISHED_BY_FLEET_MANAGER = 'MANUALLY_FINISHED_BY_FLEET_MANAGER',
}

export interface TripSummary {
  tripDate: string | null;
  profileId: string;
  driverName: string | null;
  vehicle: string | null;
  routeName: string | null;
  origin: string | null;
  destination: string | null;
  plannedDistance: number | null;
  distance: number | null;
  co2Emission: number | null;
  plannedCo2Emission: number | null;
  fuelConsumption: number | null;
  plannedFuelConsumption: number | null;
  idleTime: number | null;
  plannedIdleTime: number | null;
  totalTime: number | null;
  plannedTime: number | null;
  driverId: string | undefined;
  routeId: string | undefined;
  tripId: string | null;
  rioId: string | null;
  idleTimeFormatted?: string | null;
  totalTimeFormatted?: string | null;
  plannedIdleTimeFormatted?: string | null;
  plannedTimeFormatted?: string | null;
}

export interface TripsPage {
  trips: TripSummary[];
  page: number;
  totalPages: number;
  totalCo2Emission: number;
  totalDistance: number;
  totalFuelConsumption: number;
  totalIdleTime: number;
  totalTime: number;
  totalTrips: number;
  plannedDistance: number;
  plannedFuelConsumption: number;
  plannedCo2Emission: number;
  plannedTime: number;
  initialDate: string;
  finalDate: string;
}
